import React, { useCallback, useEffect, useState } from "react";
import { CalendarDaysIcon, ArrowPathIcon, ShieldCheckIcon } from "@heroicons/react/24/outline";
import { api } from "../utils/api";
import { getCalendarPlatform } from "../utils/calendarPlatform";
import { toastError, toastSuccess } from "../utils/toast";

const CalendarSyncCard = () => {
  const [status, setStatus] = useState({ loading: true, feedAttivo: false });
  const [actionLoading, setActionLoading] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const platform = getCalendarPlatform();
  const isAndroid = platform === "android";
  const isPreviewHost =
    typeof window !== "undefined" &&
    /\.vercel\.app$/i.test(window.location.hostname) &&
    window.location.hostname !== "plannerturni.it";

  const loadStatus = useCallback(async () => {
    try {
      const response = await api.get("/calendario/stato");
      setStatus({
        loading: false,
        feedAttivo: Boolean(response.data?.feed_attivo),
        googleDisponibile: Boolean(response.data?.google_disponibile),
        googleAttivo: Boolean(response.data?.google_attivo),
      });
    } catch (error) {
      // La scheda non deve compromettere la bacheca se il calendario non è
      // ancora configurato sul backend o la sessione è in transizione.
      setStatus(
        isPreviewHost
          ? { loading: false, preview: true, feedAttivo: false, googleDisponibile: true }
          : { loading: false, unavailable: true, feedAttivo: false }
      );
    }
  }, [isPreviewHost]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const addAppleCalendar = async (replace = false) => {
    if (status.preview) {
      toastSuccess("Anteprima: su iPhone si aprirà l'aggiunta del calendario Turni Planner.");
      return;
    }
    setActionLoading(true);
    try {
      const response = await api.post("/calendario/feed", { replace });
      const target = response.data?.webcal_url || response.data?.feed_url;
      if (!target) throw new Error("Link calendario non disponibile");
      setStatus((current) => ({ ...current, feedAttivo: true }));
      toastSuccess("Apriamo Calendario: conferma l'aggiunta del calendario Turni Planner.");
      window.location.assign(target);
    } catch (error) {
      if (error.response?.data?.code === "FEED_GIA_ATTIVO") {
        setManageOpen(true);
      } else {
        toastError(
          error.response?.data?.message ||
            "Non è stato possibile preparare il calendario. Riprova tra poco."
        );
      }
    } finally {
      setActionLoading(false);
    }
  };

  const disconnectFeed = async () => {
    setActionLoading(true);
    try {
      await api.post("/calendario/feed/disattiva");
      setStatus((current) => ({ ...current, feedAttivo: false }));
      setManageOpen(false);
      toastSuccess("Collegamento calendario disattivato.");
    } catch (error) {
      toastError(error.response?.data?.message || "Impossibile disattivare il collegamento.");
    } finally {
      setActionLoading(false);
    }
  };

  const connectGoogleCalendar = async () => {
    if (status.preview) {
      toastSuccess("Anteprima: su Android si aprirà il consenso Google Calendar.");
      return;
    }
    setActionLoading(true);
    try {
      const response = await api.post("/calendario/google/authorize");
      const target = response.data?.authorize_url;
      if (!target) throw new Error("Autorizzazione Google non disponibile");
      window.location.assign(target);
    } catch (error) {
      toastError(
        error.response?.data?.message ||
          "Non è stato possibile avviare il collegamento con Google Calendar."
      );
    } finally {
      setActionLoading(false);
    }
  };

  const disconnectGoogleCalendar = async () => {
    setActionLoading(true);
    try {
      await api.post("/calendario/google/disattiva");
      setStatus((current) => ({ ...current, googleAttivo: false }));
      setManageOpen(false);
      toastSuccess("Collegamento Google Calendar disattivato.");
    } catch (error) {
      toastError(error.response?.data?.message || "Impossibile disattivare Google Calendar.");
    } finally {
      setActionLoading(false);
    }
  };

  if (status.loading || status.unavailable) return null;

  const primaryTitle = isAndroid
    ? "Collega Google Calendar"
    : "Aggiungi al calendario";
  const primaryDescription = isAndroid
    ? "Aggiungi e mantieni aggiornati turni, pulizie e conduzioni in Google Calendar."
    : "Turni di postazione, pulizie e conduzioni, sempre aggiornati sul tuo telefono.";

  return (
    <section className="mt-5 rounded-xl border border-primary-200 bg-primary-50/70 p-4 sm:p-5">
      <div className="flex gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-primary-700 shadow-sm">
          <CalendarDaysIcon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h4 className="text-base font-semibold text-gray-900">{primaryTitle}</h4>
          <p className="mt-1 text-sm leading-5 text-gray-600">{primaryDescription}</p>
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
        {isAndroid ? (
          <button
            type="button"
            onClick={connectGoogleCalendar}
            disabled={actionLoading || status.googleAttivo}
            className="inline-flex min-h-11 items-center justify-center rounded-lg bg-primary-600 px-4 text-sm font-semibold text-white transition hover:bg-primary-700 disabled:cursor-default disabled:opacity-70"
          >
            {status.googleAttivo ? "Google Calendar collegato" : "Collega Google Calendar"}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => addAppleCalendar(false)}
            disabled={actionLoading}
            className="inline-flex min-h-11 items-center justify-center rounded-lg bg-primary-600 px-4 text-sm font-semibold text-white transition hover:bg-primary-700 disabled:cursor-wait disabled:opacity-70"
          >
            {actionLoading ? "Preparazione..." : "Aggiungi i miei turni"}
          </button>
        )}
        {(status.feedAttivo || status.googleAttivo) && (
          <button
            type="button"
            onClick={() => setManageOpen((open) => !open)}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-primary-300 bg-white px-4 text-sm font-semibold text-primary-800 transition hover:bg-primary-50"
          >
            <ArrowPathIcon className="h-4 w-4" /> Gestisci collegamento
          </button>
        )}
      </div>

      {status.preview && (
        <p className="mt-3 text-xs font-medium text-primary-800">Anteprima: i collegamenti reali non sono attivi in questo ambiente di prova.</p>
      )}

      {isAndroid && (
        <p className="mt-3 text-xs leading-5 text-gray-500">
          La connessione Google aprirà una richiesta di autorizzazione sicura: Planner potrà gestire solo il calendario “Turni Planner”.
        </p>
      )}

      {manageOpen && (
        <div className="mt-4 rounded-lg border border-primary-200 bg-white p-3 text-sm text-gray-700">
          <div className="flex items-start gap-2">
            <ShieldCheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-primary-600" />
            <p>Il link è personale. Se cambi telefono o pensi che sia stato condiviso, sostituiscilo o disattivalo.</p>
          </div>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row">
            {status.feedAttivo && (
              <>
                <button
                  type="button"
                  onClick={() => addAppleCalendar(true)}
                  disabled={actionLoading}
                  className="min-h-10 rounded-md border border-primary-300 px-3 text-sm font-medium text-primary-800 hover:bg-primary-50 disabled:opacity-60"
                >
                  Sostituisci collegamento Apple
                </button>
                <button
                  type="button"
                  onClick={disconnectFeed}
                  disabled={actionLoading}
                  className="min-h-10 rounded-md px-3 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-60"
                >
                  Disattiva Apple
                </button>
              </>
            )}
            {status.googleAttivo && (
              <button
                type="button"
                onClick={disconnectGoogleCalendar}
                disabled={actionLoading}
                className="min-h-10 rounded-md px-3 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-60"
              >
                Disattiva Google Calendar
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default CalendarSyncCard;
